import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  actionItemsApi,
  type ActionItemFilters,
  type CreateActionItemInput,
  type UpdateActionItemInput,
} from '@/api/actionItems'
import { useTenant } from '@/providers/TenantProvider'

/** Everything under this key is refetched after any change (z8nrz7fkfh). */
const ROOT = 'action-items'

/** The list for the filters applied on Go; `null` filters ask for nothing. */
export function useActionItems(filters: ActionItemFilters | null) {
  const { selectedTenantId } = useTenant()
  return useQuery({
    queryKey: [ROOT, 'list', selectedTenantId, filters] as const,
    enabled: !!selectedTenantId && filters !== null,
    queryFn: () => actionItemsApi.list(filters ?? {}),
  })
}

/** The Action (n) badge: a form's items not yet completed. */
export function useActionItemOpenCount(submissionId: string | null, enabled = true) {
  const { selectedTenantId } = useTenant()
  return useQuery({
    queryKey: [ROOT, 'open-count', selectedTenantId, submissionId] as const,
    enabled: enabled && !!selectedTenantId && !!submissionId,
    queryFn: () => actionItemsApi.openCount(submissionId as string),
  })
}

/** People and groups an item can be assigned to, and filtered by. */
export function useActionItemAssignees(enabled = true) {
  const { selectedTenantId } = useTenant()
  return useQuery({
    queryKey: [ROOT, 'assignees', selectedTenantId] as const,
    enabled: enabled && !!selectedTenantId,
    queryFn: () => actionItemsApi.assignees(),
  })
}

function useInvalidate() {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({ queryKey: [ROOT] })
}

export function useCreateActionItem() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: (input: CreateActionItemInput) => actionItemsApi.create(input),
    onSuccess: invalidate,
  })
}

export function useUpdateActionItem() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateActionItemInput }) => actionItemsApi.update(id, input),
    onSuccess: invalidate,
  })
}

export function useRemoveActionItems() {
  const invalidate = useInvalidate()
  return useMutation({
    // One at a time, so a refusal names the item it was about.
    mutationFn: async (ids: string[]) => {
      for (const id of ids) await actionItemsApi.remove(id)
    },
    onSettled: invalidate,
  })
}

export function useAttachToActionItem() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: async ({ id, files }: { id: string; files: File[] }) => {
      for (const file of files) await actionItemsApi.attach(id, file)
    },
    onSettled: invalidate,
  })
}
